// 团队心力资本初评 - 双轨融合计分（算法70% + 自述30%）

export interface SelectedScene {
  name: string
  rank: number
}

export interface SelfReport {
  cohesionScore: number      // 1-10
  resiliencePercent: number  // 0-100
  resilienceScore: number    // 自动: percent/10
  growthAcceptance: number   // 1-10
  growthResistanceRaw: number // 1-10（原值，需反向）
  growthResistanceScore: number // 反向: 11-raw
  growthInitiativePercent: number // 0-100
  growthInitiativeScore: number // 自动: percent/10
}

export interface AssessmentInput {
  scenes: SelectedScene[]
  industry: string
  teamSize: string
  contactName: string
  companyName: string
  foundedYear: string
  annualRevenue: string
  phone: string
  email: string
  selfReport: SelfReport
}

export interface DimensionResult {
  algorithmScore: number   // 算法客观分
  selfScore: number        // 自述分（10分制）
  finalScore: number       // 融合分 = algo*0.7 + self*0.3
  benchmark: number        // 行业基准
  gapType: 'overestimate' | 'underestimate' | 'consistent'  // 感知差距
  gapDesc: string          // 差距描述
}

export interface AssessmentResult {
  cohesion: DimensionResult
  resilience: DimensionResult
  growth: DimensionResult
  overallIndex: number
  overallGapType: string
  overallGapDesc: string
  weakestDimension: string
  riskWarning: string
  riskLevel: string
  scenes: SelectedScene[]
  industry: string
  teamSize: string
  companyName: string
  contactName: string
  phone: string
  email: string
}

/* ─── 场景系数 ─── */
const sceneCoefficients: Record<string, { cohesion: number; resilience: number; growth: number }> = {
  '团队重组/合并': { cohesion: 1.3, resilience: 1.1, growth: 0.85 },
  '业绩冲刺高压': { cohesion: 1.1, resilience: 1.3, growth: 1.0 },
  'AI业务重构': { cohesion: 0.85, resilience: 1.1, growth: 1.3 },
  '重大人事变动': { cohesion: 1.3, resilience: 1.3, growth: 0.85 },
  '品牌/公共危机': { cohesion: 1.0, resilience: 1.3, growth: 0.85 },
  '组织文化重塑': { cohesion: 1.1, resilience: 1.1, growth: 1.1 },
}

const rankWeights: Record<number, number> = { 1: 1.0, 2: 0.6, 3: 0.3 }

const industryAdjustments: Record<string, { cohesion: number; resilience: number; growth: number }> = {
  '互联网': { cohesion: 3, resilience: -2, growth: 8 },
  '制造': { cohesion: 5, resilience: 5, growth: -5 },
  '金融': { cohesion: 2, resilience: 8, growth: -3 },
  '医药/生物': { cohesion: 4, resilience: 3, growth: 0 },
  '零售/消费': { cohesion: 0, resilience: 5, growth: 2 },
  '其他': { cohesion: 0, resilience: 0, growth: 0 },
}

const sizeAdjustments: Record<string, { cohesion: number; resilience: number; growth: number }> = {
  '50人以下': { cohesion: 5, resilience: -2, growth: 3 },
  '50-200人': { cohesion: 2, resilience: 0, growth: 0 },
  '200-1000人': { cohesion: -2, resilience: 3, growth: -2 },
  '1000人以上': { cohesion: -5, resilience: 5, growth: -5 },
}

const industryBenchmarks: Record<string, { cohesion: number; resilience: number; growth: number }> = {
  '互联网': { cohesion: 68, resilience: 58, growth: 70 },
  '制造': { cohesion: 70, resilience: 65, growth: 57 },
  '金融': { cohesion: 67, resilience: 68, growth: 59 },
  '医药/生物': { cohesion: 69, resilience: 63, growth: 62 },
  '零售/消费': { cohesion: 65, resilience: 65, growth: 64 },
  '其他': { cohesion: 65, resilience: 60, growth: 62 },
}

/* ─── 感知差距诊断 ─── */
function calcGapType(selfScore: number, algoScore: number): { gapType: DimensionResult['gapType']; gapDesc: string } {
  // 算法分折算到10分制对比
  const algoOn10 = algoScore / 9.1
  const diff = selfScore - algoOn10

  if (diff >= 2) {
    return {
      gapType: 'overestimate',
      gapDesc: '您对团队的感知高于数据基准，建议关注数据揭示的潜在薄弱项',
    }
  } else if (diff <= -2) {
    return {
      gapType: 'underestimate',
      gapDesc: '您对团队现状的感知比数据基准更严格，需要区分「真实问题」与「变革期焦虑放大」',
    }
  }
  return {
    gapType: 'consistent',
    gapDesc: '您的感知与数据基准高度一致，说明贵司管理者对团队状况有清晰判断',
  }
}

/* ─── 多场景综合系数 ─── */
function calculateCompositeCoefficients(scenes: SelectedScene[]) {
  let cSum = 0, rSum = 0, gSum = 0
  for (const s of scenes) {
    const coeffs = sceneCoefficients[s.name]
    const w = rankWeights[s.rank]
    cSum += coeffs.cohesion * w
    rSum += coeffs.resilience * w
    gSum += coeffs.growth * w
  }
  return { cohesion: cSum, resilience: rSum, growth: gSum }
}

/* ─── 归一化 ─── */
function normalizeCoefficient(composite: number): number {
  const minC = 0.85, maxC = 2.47
  const targetMin = 0.85, targetMax = 1.4
  if (composite <= minC) return targetMin
  if (composite >= maxC) return targetMax
  return targetMin + ((composite - minC) / (maxC - minC)) * (targetMax - targetMin)
}

/* ─── 复合预警 ─── */
function generateCompositeWarning(scenes: SelectedScene[], cohesion: number, resilience: number, growth: number): { warning: string; level: string } {
  const sorted = [...scenes].sort((a, b) => a.rank - b.rank)
  const primary = sorted[0]?.name || ''
  const secondary = sorted[1]?.name || ''
  const dims = [
    { name: '凝聚力资本', score: cohesion },
    { name: '抗逆力资本', score: resilience },
    { name: '成长力资本', score: growth },
  ]
  dims.sort((a, b) => a.score - b.score)
  const weakest = dims[0]

  const sceneSet = new Set(scenes.map(s => s.name))

  if (sceneSet.has('AI业务重构') && sceneSet.has('业绩冲刺高压') && sceneSet.has('团队重组/合并') && primary === 'AI业务重构') {
    return { warning: `AI重构叠加业绩冲刺与重组压力，贵团队处于**三重变革同时推进**的高压状态。技术适应力的滞后将直接传导至业绩达成（影响周期缩短至2周内），同时重组带来的信任损耗会加剧执行走样风险。`, level: '高' }
  }
  if (sceneSet.has('业绩冲刺高压') && sceneSet.has('重大人事变动') && primary === '业绩冲刺高压') {
    return { warning: `业绩冲刺期叠加核心人员变动，团队**抗逆力**正承受双重考验。新任管理者需在1-2周内完成信任建立，否则冲刺目标将面临滞后风险。`, level: '高' }
  }
  if (sceneSet.has('团队重组/合并') && sceneSet.has('组织文化重塑') && primary === '团队重组/合并') {
    return { warning: `重组与文化重塑同步推进，团队**凝聚力**面临「旧体系瓦解、新共识未立」的真空期。信息透明度需提升至日常沟通的3倍以上，方可抵消不确定性带来的防御性沉默。`, level: '高' }
  }
  if (sceneSet.has('重大人事变动') && sceneSet.has('AI业务重构') && primary === '重大人事变动') {
    return { warning: `核心变动叠加AI转型，团队面临**双重不确定性**。老员工对「替代」的焦虑将被放大，技术焦虑与职业安全感叠加，需在2周内出台明确的人员转型路径说明。`, level: '高' }
  }
  if (sceneSet.has('品牌/公共危机') && sceneSet.has('业绩冲刺高压') && primary === '品牌/公共危机') {
    return { warning: `外部品牌危机与内部业绩高压形成**夹击效应**，团队抗逆力消耗速度是单一场景的1.8倍。建议在冲刺动员前先进行团队心理安全度评估，否则士气滑坡将快于预期。`, level: '高' }
  }

  if (scenes.length === 2) {
    return { warning: `贵团队正处于**${primary}**为主、**${secondary}**为辅的复合压力周期。**${weakest.name}**（${weakest.score}分）是最需优先补给的心力短板，建议在4周内启动针对性干预。`, level: '中高' }
  }
  return { warning: `贵团队正处于**${primary}**为主、**${secondary}**与**${sorted[2]?.name || ''}**叠加的**三重压力周期**。**${weakest.name}**（${weakest.score}分）是最需优先补给的心力短板，建议在4周内启动针对性干预。`, level: '中高' }
}

/* ─── 融合自述的复合预警 ─── */
function generateFusionWarning(dim: string, gapType: DimensionResult['gapType'], selfScore: number, algoScore: number): string {
  if (gapType === 'underestimate') {
    return `您对团队${dim}的自评（${selfScore.toFixed(1)}分）低于数据基准（${algoScore}分）。变革期管理者的焦虑可能放大了问题感知，建议先以客观数据为基线，避免在过度焦虑下仓促决策。`
  } else if (gapType === 'overestimate') {
    return `您对团队${dim}的自评（${selfScore.toFixed(1)}分）高于数据基准（${algoScore}分）。数据提示该维度存在潜在薄弱环节，建议在问题显性化前进行预防性摸底。`
  }
  return `您对团队${dim}的感知与数据基准高度吻合，说明贵司管理者对团队状况有准确判断，干预方向容易达成共识。`
}

/* ─── 主计算函数 ─── */
export function calculateAssessment(input: AssessmentInput): AssessmentResult {
  const sr = input.selfReport
  const industryAdj = industryAdjustments[input.industry] || industryAdjustments['其他']
  const sizeAdj = sizeAdjustments[input.teamSize] || sizeAdjustments['50-200人']
  const benchmarks = industryBenchmarks[input.industry] || industryBenchmarks['其他']

  // Step 1: 算法分
  const baseC = 65 + industryAdj.cohesion + sizeAdj.cohesion
  const baseR = 60 + industryAdj.resilience + sizeAdj.resilience
  const baseG = 62 + industryAdj.growth + sizeAdj.growth

  const composite = calculateCompositeCoefficients(input.scenes)
  const mC = normalizeCoefficient(composite.cohesion)
  const mR = normalizeCoefficient(composite.resilience)
  const mG = normalizeCoefficient(composite.growth)

  const algoC = Math.round(Math.max(30, Math.min(90, baseC * mC)))
  const algoR = Math.round(Math.max(30, Math.min(90, baseR * mR)))
  const algoG = Math.round(Math.max(30, Math.min(90, baseG * mG)))

  // Step 2: 自述分（四舍五入取整数）
  const selfC = sr.cohesionScore
  const selfR = sr.resilienceScore
  const selfG = Math.round((sr.growthAcceptance + sr.growthResistanceScore + sr.growthInitiativeScore) / 3)

  // Step 3: 融合分 = 算法×0.7 + 自述×0.3（自述从10分制映射到90分制）
  const finalC = Math.round(Math.max(30, Math.min(90, algoC * 0.7 + (selfC / 10) * 90 * 0.3)))
  const finalR = Math.round(Math.max(30, Math.min(90, algoR * 0.7 + (selfR / 10) * 90 * 0.3)))
  const finalG = Math.round(Math.max(30, Math.min(90, algoG * 0.7 + (selfG / 10) * 90 * 0.3)))

  // Step 4: 感知差距
  const gapC = calcGapType(selfC, algoC)
  const gapR = calcGapType(selfR, algoR)
  const gapG = calcGapType(selfG, algoG)

  // 综合感知差距
  const gapCounts = { overestimate: 0, underestimate: 0, consistent: 0 }
  ;[gapC, gapR, gapG].forEach(g => { gapCounts[g.gapType]++ })

  let overallGapType = '感知一致'
  let overallGapDesc = '您的感知与数据基准整体一致，管理者对团队状况判断准确。'
  if (gapCounts.underestimate >= 2) {
    overallGapType = '低估偏差'
    overallGapDesc = '您对团队整体评估偏谨慎，三项自评多低于数据基准。这种「比数据更悲观」的感知可能是变革期焦虑的自然反应，建议以客观数据为基线制定干预方案。'
  } else if (gapCounts.overestimate >= 2) {
    overallGapType = '高估偏差'
    overallGapDesc = '您对团队整体评估偏乐观，自评高于数据基准。数据揭示了潜在的薄弱环节，建议在问题显性化前进行预防性摸底。'
  }

  // 风险预警
  const { warning: baseWarning, level } = generateCompositeWarning(input.scenes, finalC, finalR, finalG)

  // 融合自述的复合预警
  const fusionWarnings = [
    generateFusionWarning('凝聚力', gapC.gapType, selfC, algoC),
    generateFusionWarning('抗逆力', gapR.gapType, selfR, algoR),
    generateFusionWarning('成长力', gapG.gapType, selfG, algoG),
  ]
  const fusionWarning = fusionWarnings.join('\n\n')

  // 最终预警：基础预警 + 融合校准
  const fullWarning = `${baseWarning}\n\n——\n\n📊 管理者感知校准：\n\n${fusionWarning}`

  const weakestDim = finalC <= finalR && finalC <= finalG ? '凝聚力资本' : finalR <= finalG ? '抗逆力资本' : '成长力资本'

  return {
    cohesion: { algorithmScore: algoC, selfScore: selfC, finalScore: finalC, benchmark: benchmarks.cohesion, gapType: gapC.gapType, gapDesc: gapC.gapDesc },
    resilience: { algorithmScore: algoR, selfScore: selfR, finalScore: finalR, benchmark: benchmarks.resilience, gapType: gapR.gapType, gapDesc: gapR.gapDesc },
    growth: { algorithmScore: algoG, selfScore: selfG, finalScore: finalG, benchmark: benchmarks.growth, gapType: gapG.gapType, gapDesc: gapG.gapDesc },
    overallIndex: Math.round((finalC + finalR + finalG) / 3 * 10) / 10,
    overallGapType,
    overallGapDesc,
    weakestDimension: weakestDim,
    riskWarning: fullWarning,
    riskLevel: level,
    scenes: input.scenes,
    industry: input.industry,
    teamSize: input.teamSize,
    companyName: input.companyName,
    contactName: input.contactName,
    phone: input.phone,
    email: input.email,
  }
}
